/**
 * Guide body structure.
 *
 * `contentJson` is NOT a block list — it is a section-keyed document, and the
 * live renderer (ResourcesDetailPage) dereferences the sections below WITHOUT
 * a null guard. A missing one throws in the browser and the page renders as an
 * error boundary, which is why publish is gated on them here as well as on the
 * server.
 *
 * Kept in step with REQUIRED_SECTIONS_TO_PUBLISH in backend/src/routes/guides.ts.
 */
export const REQUIRED_SECTIONS: Record<string, string[]> = {
  'hiring-guide': ['hook', 'seo', 'roleOverview', 'hiringProcess', 'relatedResourceSlugs'],
  'skill-guide': [
    'hook', 'seo', 'whatIsIt', 'stepByStep', 'realWorldExample',
    'commonMistakes', 'proTips', 'careerImpact', 'relatedResourceSlugs',
  ],
  'career-guide': ['hook', 'seo', 'sections', 'relatedResourceSlugs'],
};

/** Readable labels, so the editor does not show camelCase keys to an author. */
export const SECTION_LABELS: Record<string, string> = {
  hook: 'Hook',
  seo: 'SEO',
  roleOverview: 'Role overview',
  hiringProcess: 'Hiring process',
  whatIsIt: 'What is it',
  stepByStep: 'Step by step',
  realWorldExample: 'Real-world example',
  commonMistakes: 'Common mistakes',
  proTips: 'Pro tips',
  careerImpact: 'Career impact',
  sections: 'Sections',
  relatedResourceSlugs: 'Related guides',
  title: 'Title',
  slug: 'Slug',
  pageType: 'Page type',
  excerpt: 'Excerpt',
};

/**
 * Keys the editor does not offer as body sections because they are owned by
 * the inspector and synced into the JSON by the server on save.
 */
export const MIRRORED_KEYS = new Set(['title', 'slug', 'pageType', 'excerpt']);

export type SectionKind = 'text' | 'list' | 'json';

/** How a section value should be edited, inferred from what it holds. */
export function sectionKind(value: unknown): SectionKind {
  if (typeof value === 'string') return 'text';
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) return 'list';
  return 'json';
}

export interface ParsedGuide {
  ok: boolean;
  error?: string;
  obj: Record<string, unknown>;
}

export function parseGuide(contentJson: string): ParsedGuide {
  if (!contentJson.trim()) return { ok: true, obj: {} };
  try {
    const obj = JSON.parse(contentJson);
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return { ok: false, error: 'The guide body must be a JSON object.', obj: {} };
    }
    return { ok: true, obj };
  } catch (e) {
    return { ok: false, error: (e as Error).message, obj: {} };
  }
}

/** Every section present in the document, required ones first and in order. */
export function orderedSections(obj: Record<string, unknown>, pageType: string): string[] {
  const required = REQUIRED_SECTIONS[pageType] ?? [];
  const rest = Object.keys(obj).filter((k) => !required.includes(k) && !MIRRORED_KEYS.has(k));
  return [...required, ...rest];
}

/** Sections the renderer needs that this document does not have. */
export function missingRequired(obj: Record<string, unknown>, pageType: string): string[] {
  return (REQUIRED_SECTIONS[pageType] ?? []).filter(
    (k) => obj[k] === undefined || obj[k] === null,
  );
}

/** Every string leaf, for word count and read time. */
function collectText(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) value.forEach((v) => collectText(v, out));
  else if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((v) => collectText(v, out));
  }
  return out;
}

/** The public template displays read time, so the editor shows the same number. */
export function readingStats(obj: Record<string, unknown>): { words: number; minutes: number } {
  const words = collectText(obj)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;
  return { words, minutes: Math.max(1, Math.round(words / 200)) };
}

/**
 * The same stats for a guide authored as HTML, read from the body a reader actually gets.
 *
 * An HTML guide still carries its JSON envelope, but that holds the template fields the import
 * happened to fill, not the body, so counting it warns a long guide that it reads as a stub.
 */
export function htmlReadingStats(html: string): { words: number; minutes: number } {
  const text = html
    .replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    // Other entities sit inside a word (don&rsquo;t) or stand alone (&amp;): dropping them keeps
    // the first one word and stops the second counting as one.
    .replace(/&[a-z0-9#]+;/gi, '');
  const words = text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
  return { words, minutes: Math.max(1, Math.round(words / 200)) };
}

/** Slug the public route uses, derived the same way the extractor does. */
export function deriveSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * Where a published guide lives, so publish can name its destination.
 *
 * These are the real public routes — `src/pages/academy-guides/[slug].tsx` and its two siblings on
 * the main site, and the same map the BFF builds preview links from (`TYPE_PATH` in
 * `backend/src/routes/guides.ts`). It used to return `/resources/{academy|career|hiring}/{slug}`,
 * which is not a route on the site at all, so the publish, archive and delete dialogs each quoted a
 * URL that 404s while telling the operator what would happen to it.
 */
export function publicPath(pageType: string, slug: string): string {
  const segment =
    pageType === 'skill-guide'
      ? 'academy-guides'
      : pageType === 'career-guide'
        ? 'career-guides'
        : 'hiring-guides';
  return `/${segment}/${slug}`;
}
